import { Component, Inject, OnInit } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { FacePhoto, FaceSummary, FaceValidation } from '../../../models/Personal/FaceTrace';
import { PersonalService } from '../../../services/modules/personal.service';
import { SnackBarService } from '../../../services/utils/snackBar.service';

export interface DialogFaceTraceData {
  personalCompanyInfoId: number | string;
  permissions: {
    canView: boolean;
    canDelete: boolean;
    canReprocess: boolean;
  };
  s3Bucket: string;
  employeeName: string;
}

@Component({
  selector: 'app-dialog-face-trace',
  templateUrl: './dialog-face-trace.component.html',
  styleUrls: ['./dialog-face-trace.component.scss']
})
export class DialogFaceTraceComponent implements OnInit {

  photos: FacePhoto[] = [];
  faceSummary?: FaceSummary;
  faceValidation?: FaceValidation;

  loadingPhotos = true;
  loadingSummary = true;
  loadingValidation = true;

  selectedPhotoId?: number;

  constructor(
    private dialogRef: MatDialogRef<DialogFaceTraceComponent>,
    @Inject(MAT_DIALOG_DATA) public data: DialogFaceTraceData,
    private personalService: PersonalService,
    private snackBar: SnackBarService
  ) {}

  ngOnInit(): void {
    this.loadPhotos();
    this.loadFaceSummary();
    this.loadFaceValidation();
  }

  loadPhotos(): void {
    if (!this.data.permissions.canView) { return; }
    this.loadingPhotos = true;
    this.personalService.getFacePhotos(this.data.personalCompanyInfoId, true).subscribe(
      (res: FacePhoto[]) => {
        this.photos = res || [];
        this.loadingPhotos = false;
      },
      (err) => {
        this.loadingPhotos = false;
        if (err.status === 403 || err.status === 404) {
           this.photos = [];
        } else if (err.error && err.error.Error) {
          this.snackBar.openWarning(err.error.Error);
        }
      }
    );
  }

  selectPhoto(photo: FacePhoto | null): void {
    this.selectedPhotoId = photo ? photo.Id : undefined;
    this.loadFaceSummary();
    this.loadFaceValidation();
  }

  getSelectedPhoto(): FacePhoto | undefined {
    if (this.selectedPhotoId) {
      return this.photos.find(p => p.Id === this.selectedPhotoId);
    }
    return this.photos.length > 0 ? this.photos[0] : undefined;
  }

  deletePhoto(photo: FacePhoto): void {
    if (!this.data.permissions.canDelete) { return; }
    if (photo.IsCurrent) { return; }

    this.personalService.deleteFacePhoto(photo.Id).subscribe(
      (res) => {
        if (!res.AlreadyDeleted) {
          this.snackBar.openSuccess('Foto eliminada correctamente');
        }
        this.loadPhotos();
        this.loadFaceSummary();
        this.loadFaceValidation();
      },
      (err) => {
        if (err.error && err.error.Error) {
          this.snackBar.openWarning(err.error.Error);
        }
      }
    );
  }

  reprocessPhoto(): void {
    if (!this.data.permissions.canReprocess) { return; }
    this.personalService.reprocessPhoto(this.data.personalCompanyInfoId).subscribe(
      (res: any) => {
        if (res.enqueued) {
           this.snackBar.openSuccess('Procesamiento encolado');
           setTimeout(() => { this.loadFaceSummary(); this.loadFaceValidation(); }, 3000);
        } else {
           this.snackBar.openWarning(res.reason || 'No se pudo encolar');
        }
      },
      (err) => {
        if (err.error && err.error.Error) {
          this.snackBar.openWarning(err.error.Error);
        }
      }
    );
  }

  loadFaceSummary(): void {
    if (!this.data.permissions.canView) { return; }
    this.loadingSummary = true;
    const photoId = this.selectedPhotoId || (this.photos.length > 0 ? this.photos[0].Id : undefined);
    this.personalService.getFaceSummary(this.data.personalCompanyInfoId, photoId).subscribe(
      (res: FaceSummary) => {
        this.faceSummary = res;
        this.loadingSummary = false;
      },
      (err) => {
        this.loadingSummary = false;
        if (err.status === 403 || err.status === 404) {
          this.faceSummary = undefined;
        } else if (err.error && err.error.Error) {
          this.snackBar.openWarning(err.error.Error);
        }
      }
    );
  }

  loadFaceValidation(): void {
    if (!this.data.permissions.canView) { return; }
    this.loadingValidation = true;
    const photoId = this.selectedPhotoId || (this.photos.length > 0 ? this.photos[0].Id : undefined);
    this.personalService.getFaceValidation(this.data.personalCompanyInfoId, photoId).subscribe(
      (res: FaceValidation) => {
        this.faceValidation = res;
        this.loadingValidation = false;
      },
      (err) => {
        this.loadingValidation = false;
        if (err.status === 403 || err.status === 404) {
          this.faceValidation = undefined;
        } else if (err.error && err.error.Error) {
          this.snackBar.openWarning(err.error.Error);
        }
      }
    );
  }

  onClose(): void {
    this.dialogRef.close();
  }

  formatBytes(bytes: number): string {
      if (bytes === 0) { return '0 Bytes'; }
      const k = 1024;
      const sizes = ['Bytes', 'KB', 'MB', 'GB'];
      const i = Math.floor(Math.log(bytes) / Math.log(k));
      return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  }

  formatDateOnly(dateStr: string): string {
    if (!dateStr) { return ''; }
    try {
      const parts = dateStr.split('T');
      const dateParts = parts[0].split('-');
      if (dateParts.length === 3) {
        return `${dateParts[2]}/${dateParts[1]}/${dateParts[0]}`;
      }
    } catch (e) {}
    return dateStr;
  }

  groupRulesByCategory(rules: any[] | undefined): any[] {
    if (!rules || rules.length === 0) { return []; }

    const categories: Record<string, string[]> = {
      ENCUADRE: ['R1', 'R2', 'R3', 'R4', 'R13'],
      'POSICIÓN DEL ROSTRO': ['R5', 'R7', 'R8', 'R11', 'R12'],
      'CALIDAD DE LA IMAGEN': ['R6', 'R7b', 'R9', 'R10']
    };

    const orderedCategories = ['ENCUADRE', 'POSICIÓN DEL ROSTRO', 'CALIDAD DE LA IMAGEN'];
    const categoryMap: Record<string, any[]> = {};
    for (const cat of orderedCategories) {
      categoryMap[cat] = [];
    }

    for (const rule of rules) {
      let ruleCategory: string | null = null;
      if (rule.Section) {
        const sec = rule.Section.toUpperCase();
        if (sec.includes('ENCUADRE')) {
          ruleCategory = 'ENCUADRE';
        } else if (sec.includes('POSICI')) {
          ruleCategory = 'POSICIÓN DEL ROSTRO';
        } else if (sec.includes('CALIDAD')) {
          ruleCategory = 'CALIDAD DE LA IMAGEN';
        }
      }

      if (!ruleCategory) {
        for (const cat in categories) {
          if (categories[cat].includes(rule.Code)) {
            ruleCategory = cat;
            break;
          }
        }
      }

      ruleCategory = ruleCategory || 'OTRO';
      if (!categoryMap[ruleCategory]) {
        categoryMap[ruleCategory] = [];
      }
      categoryMap[ruleCategory].push(rule);
    }

    const result: any[] = [];
    for (const cat of orderedCategories) {
      if (categoryMap[cat] && categoryMap[cat].length > 0) {
        result.push({
          category: cat,
          rules: categoryMap[cat]
        });
      }
    }

    for (const cat in categoryMap) {
      if (!orderedCategories.includes(cat) && categoryMap[cat] && categoryMap[cat].length > 0) {
        result.push({
          category: cat,
          rules: categoryMap[cat]
        });
      }
    }

    return result;
  }

  getCategoryColor(category: string): string {
    const cat = (category || '').toUpperCase();
    if (cat.includes('ENCUADRE')) {
      return '#0070d2';
    }
    if (cat.includes('POSICI')) {
      return '#f5b025';
    }
    if (cat.includes('CALIDAD')) {
      return '#00854d';
    }
    return '#706e6b';
  }
}
